import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import playerPlaceholder from '@sorare/core/src/assets/players/placeholder.png';
import GlareEffect from '@sorare/core/src/atoms/animations/GlareEffect';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Text16, Text20 } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { FootballTeamLink } from '@sorare/core/src/routing/FootballTeamLink';
import { Link } from '@sorare/core/src/routing/Link';

import CardBonus from '@sorare/marketplace/src/components/card/CardBonus';

import BudgetCost from 'components/draft/BudgetCost';
import { positionShortNames } from 'lib/so5';

import {
  PlayerProperties_anyCard,
  PlayerProperties_anyPlayer,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })`
  justify-content: stretch;
`;
const ImageWrapper = styled.div`
  width: 40%;
  max-width: 200px;
  min-width: 110px;
`;
const Wrapper = styled(Horizontal).attrs({ as: FootballTeamLink })``;
const FlexColContainer = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: stretch;
`;
const TeamImage = styled.img`
  height: 14px;
`;

const PlayerImageWrapper = styled.div`
  position: relative;
  &::before {
    position: absolute;
    content: '';
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-neutral-100), 0) 0%,
      var(--c-neutral-100) 100%
    );
  }
`;

const CardImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
`;
const PlayerImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
`;
type Props = {
  player: PlayerProperties_anyPlayer;
  card?: PlayerProperties_anyCard;
  showCardBonusIndicator?: boolean;
  budgetValue?: number;
  pictureUrl?: Nullable<string>;
};
const PlayerProperties = ({
  player,
  pictureUrl,
  card,
  showCardBonusIndicator = true,
  budgetValue,
}: Props) => {
  const {
    displayName,
    anyPositions,
    age,
    sport,
    squaredPictureUrl: playerPictureUrl,
  } = player;
  const position = anyPositions[0];
  const { generateSportContextPath } = useSportContext();
  const { isIosApp } = useIsMobileApp();

  const {
    slug: clubSlug,
    name: clubName,
    pictureUrl: clubPictureUrl,
  } = player?.activeClub || {};
  const {
    slug: nationalTeamSlug,
    country,
    name: nationalTeamName,
    pictureUrl: nationalPictureUrl,
  } = player?.activeNationalTeam || {};

  const { pictureUrl: cardPictureUrl } = card || {};

  return (
    <Root>
      <ImageWrapper>
        {pictureUrl || cardPictureUrl ? (
          <CardImageWrapper>
            <GlareEffect
              pictureUrl={pictureUrl || cardPictureUrl || ''}
              alt="Player Image"
            />
          </CardImageWrapper>
        ) : (
          <PlayerImageWrapper>
            <PlayerImage
              src={playerPictureUrl || playerPlaceholder}
              alt="Player Image"
            />
          </PlayerImageWrapper>
        )}
      </ImageWrapper>
      <Horizontal>
        {budgetValue ? (
          <BudgetCost value={budgetValue} />
        ) : (
          <>
            <PlayerScore
              player={player}
              mode={
                sport === Sport.NBA
                  ? AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
                  : AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
              }
            />
            {card && (
              <CardBonus card={card} showBonus={showCardBonusIndicator} />
            )}
          </>
        )}
      </Horizontal>
      <Horizontal>
        <Text20 bold>
          {isIosApp ? (
            displayName
          ) : (
            <Link
              to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
                params: { slug: player.slug },
                sport: player.sport,
              })}
            >
              {displayName}
            </Link>
          )}
        </Text20>
        <Text16 color="var(--c-neutral-600)">
          <FormattedMessage {...positionShortNames[position]} />
        </Text16>
      </Horizontal>
      <FlexColContainer>
        {[
          {
            slug: clubSlug,
            name: clubName,
            image: clubPictureUrl,
            isNationalTeam: false,
          },
          {
            slug: nationalTeamSlug,
            country,
            name: nationalTeamName,
            image: nationalPictureUrl,
            isNationalTeam: true,
          },
        ]
          .filter(Boolean)
          .map(
            ({ slug, country: c, name, image, isNationalTeam }) =>
              name &&
              slug &&
              c && (
                <Wrapper
                  key={name}
                  team={{
                    slug,
                    country: c,
                  }}
                  isNationalTeam={isNationalTeam}
                  target="_blank"
                >
                  <TeamImage src={image || ''} alt="" />
                  <Text16 color="var(--c-neutral-600)">{name}</Text16>
                </Wrapper>
              )
          )}
        <Text16 color="var(--c-neutral-600)">
          <FormattedMessage
            id="PlayerDetailsDialog.PlayerAge"
            defaultMessage="{age} years old"
            values={{ age }}
          />
        </Text16>
      </FlexColContainer>
    </Root>
  );
};

PlayerProperties.fragments = {
  anyPlayer: gql`
    fragment PlayerProperties_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      age
      sport
      activeClub {
        slug
        name
        pictureUrl
      }
      activeNationalTeam {
        slug
        name
        pictureUrl
        country {
          slug
        }
      }
      squaredPictureUrl
      anyPositions
      ...PlayerScore_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<PlayerProperties_anyPlayer>,
  anyCard: gql`
    fragment PlayerProperties_anyCard on AnyCardInterface {
      slug
      pictureUrl
      ...CardBonus_WithEngine_anyCard
    }
    ${CardBonus.fragments.anyCardWithEngine}
  ` as TypedDocumentNode<PlayerProperties_anyCard>,
};

export default PlayerProperties;
